import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Divider,
  Tab,
  Tabs,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Badge,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  Dashboard,
  Group,
  Assessment,
  TrendingUp,
  TrendingDown,
  MonitorHeart,
  Map,
  Timeline,
  Warning,
  CheckCircle,
  Error,
  Info,
  Download,
  Refresh,
  Settings,
  Security,
  Storage,
  Speed,
  Cloud,
  Analytics,
  Person,
  LocationOn,
  CalendarToday,
  Notifications,
  Email,
  Phone,
  ExitToApp,
  AdminPanelSettings,
  BarChart,
  PieChart,
  ShowChart,
  DataUsage,
  Healing,
  LocalHospital,
  Favorite,
  FilterList,
  Search,
  GetApp,
  Share,
  Print
} from '@mui/icons-material';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [dashboardData, setDashboardData] = useState({
    overview: {
      totalUsers: 0,
      activeUsers: 0,
      newUsersToday: 0,
      totalReadings: 0,
      avgReadingsPerUser: 0,
      systemUptime: 0,
      dataAccuracy: 0,
      conversionRate: 0
    },
    userMetrics: {
      demographics: {
        ageGroups: [],
        genderDistribution: [],
        roleDistribution: []
      },
      activity: {
        dailyActiveUsers: [],
        weeklyActiveUsers: [],
        monthlyActiveUsers: [],
        retentionRate: 0
      },
      health: {
        hypertensionRisk: {
          low: 0,
          medium: 0,
          high: 0,
          critical: 0
        },
        averageBloodPressure: {
          systolic: 0,
          diastolic: 0
        },
        readingFrequency: {
          daily: 0,
          weekly: 0,
          monthly: 0,
          irregular: 0
        }
      }
    },
    geographic: {
      regions: [],
      cities: [],
      countries: []
    },
    system: {
      performance: {
        responseTime: 0,
        errorRate: 0,
        uptime: 0,
        throughput: 0
      },
      storage: {
        totalData: 0,
        usedSpace: 0,
        growthRate: 0
      },
      security: {
        threats: 0,
        vulnerabilities: 0,
        compliance: 0
      }
    },
    alerts: [],
    recentActivity: []
  });

  // Mock data - in real app, this would come from API
  useEffect(() => {
    const timer = setTimeout(() => {
      setDashboardData({
        overview: {
          totalUsers: 1247,
          activeUsers: 892,
          newUsersToday: 23,
          totalReadings: 18567,
          avgReadingsPerUser: 14.9,
          systemUptime: 99.8,
          dataAccuracy: 97.5,
          conversionRate: 71.5
        },
        userMetrics: {
          demographics: {
            ageGroups: [
              { range: '18-24', count: 145, percentage: 11.6 },
              { range: '25-34', count: 287, percentage: 23.0 },
              { range: '35-44', count: 342, percentage: 27.4 },
              { range: '45-54', count: 298, percentage: 23.9 },
              { range: '55-64', count: 125, percentage: 10.0 },
              { range: '65+', count: 50, percentage: 4.0 }
            ],
            genderDistribution: [
              { gender: 'Male', count: 623, percentage: 50.0 },
              { gender: 'Female', count: 578, percentage: 46.3 },
              { gender: 'Other', count: 46, percentage: 3.7 }
            ],
            roleDistribution: [
              { role: 'Patient', count: 1089, percentage: 87.3 },
              { role: 'Healthcare Provider', count: 124, percentage: 9.9 },
              { role: 'Researcher', count: 34, percentage: 2.8 }
            ]
          },
          activity: {
            dailyActiveUsers: [
              { date: '2025-01-01', users: 234 },
              { date: '2025-01-02', users: 267 },
              { date: '2025-01-03', users: 298 },
              { date: '2025-01-04', users: 312 },
              { date: '2025-01-05', users: 287 },
              { date: '2025-01-06', users: 345 },
              { date: '2025-01-07', users: 398 },
              { date: '2025-01-08', users: 456 }
            ],
            weeklyActiveUsers: 1123,
            monthlyActiveUsers: 1247,
            retentionRate: 68.5
          },
          health: {
            hypertensionRisk: {
              low: 523,
              medium: 387,
              high: 234,
              critical: 67
            },
            averageBloodPressure: {
              systolic: 128.5,
              diastolic: 82.3
            },
            readingFrequency: {
              daily: 234,
              weekly: 456,
              monthly: 378,
              irregular: 179
            }
          }
        },
        geographic: {
          regions: [
            { name: 'London', users: 298, readings: 4567, avgBP: '125/80' },
            { name: 'Manchester', users: 187, readings: 2876, avgBP: '130/85' },
            { name: 'Birmingham', users: 156, readings: 2234, avgBP: '127/82' },
            { name: 'Leeds', users: 123, readings: 1789, avgBP: '132/87' },
            { name: 'Glasgow', users: 98, readings: 1456, avgBP: '124/79' },
            { name: 'Liverpool', users: 89, readings: 1234, avgBP: '129/84' },
            { name: 'Edinburgh', users: 76, readings: 1123, avgBP: '126/81' },
            { name: 'Bristol', users: 67, readings: 987, avgBP: '128/83' },
            { name: 'Cardiff', users: 54, readings: 834, avgBP: '131/86' },
            { name: 'Newcastle', users: 45, readings: 678, avgBP: '125/80' }
          ],
          cities: [
            { name: 'London', users: 298, growth: 15.2 },
            { name: 'Manchester', users: 187, growth: 8.7 },
            { name: 'Birmingham', users: 156, growth: 12.3 },
            { name: 'Leeds', users: 123, growth: 6.8 },
            { name: 'Glasgow', users: 98, growth: 9.2 }
          ],
          countries: [
            { name: 'England', users: 1034, percentage: 82.9 },
            { name: 'Scotland', users: 174, percentage: 14.0 },
            { name: 'Wales', users: 34, percentage: 2.7 },
            { name: 'Northern Ireland', users: 5, percentage: 0.4 }
          ]
        },
        system: {
          performance: {
            responseTime: 234,
            errorRate: 0.8,
            uptime: 99.8,
            throughput: 1234
          },
          storage: {
            totalData: 4.7,
            usedSpace: 2.3,
            growthRate: 12.5
          },
          security: {
            threats: 2,
            vulnerabilities: 0,
            compliance: 98.5
          }
        },
        alerts: [
          {
            id: 1,
            type: 'warning',
            title: 'High Blood Pressure Alert',
            message: '67 users have critical hypertension readings',
            timestamp: '2025-01-08T10:30:00Z',
            priority: 'high'
          },
          {
            id: 2,
            type: 'info',
            title: 'System Performance',
            message: 'Response time increased by 15% in last hour',
            timestamp: '2025-01-08T09:45:00Z',
            priority: 'medium'
          },
          {
            id: 3,
            type: 'success',
            title: 'Data Backup Complete',
            message: 'Daily backup completed successfully',
            timestamp: '2025-01-08T06:00:00Z',
            priority: 'low'
          }
        ],
        recentActivity: [
          {
            id: 1,
            type: 'user_registration',
            user: 'John Smith',
            action: 'registered',
            timestamp: '2025-01-08T11:45:00Z',
            details: 'New user registration from London'
          },
          {
            id: 2,
            type: 'blood_pressure_reading',
            user: 'Sarah Johnson',
            action: 'recorded reading',
            timestamp: '2025-01-08T11:30:00Z',
            details: '145/95 mmHg - High risk alert triggered'
          },
          {
            id: 3,
            type: 'data_export',
            user: 'Dr. Michael Brown',
            action: 'exported data',
            timestamp: '2025-01-08T11:15:00Z',
            details: 'Patient data exported for research'
          }
        ]
      });
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [dateRange, selectedMetric]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleExportData = () => {
    const exportData = {
      overview: dashboardData.overview,
      userMetrics: dashboardData.userMetrics,
      geographic: dashboardData.geographic,
      system: dashboardData.system,
      exportDate: new Date().toISOString(),
      exportedBy: user?.email
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-analytics-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'warning': return <Warning color="warning" />;
      case 'error': return <Error color="error" />;
      case 'success': return <CheckCircle color="success" />;
      default: return <Info color="info" />;
    }
  };

  const getAlertColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const OverviewTab = () => (
    <Grid container spacing={3}>
      {/* Key Metrics */}
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2 }}>
              <Group />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {dashboardData.overview.totalUsers.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Users
            </Typography>
            <Typography variant="body2" color="success.main">
              +{dashboardData.overview.newUsersToday} today
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2 }}>
              <TrendingUp />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {dashboardData.overview.activeUsers.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Users
            </Typography>
            <Typography variant="body2" color="success.main">
              {((dashboardData.overview.activeUsers / dashboardData.overview.totalUsers) * 100).toFixed(1)}% active
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 2 }}>
              <MonitorHeart />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {dashboardData.overview.totalReadings.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Readings
            </Typography>
            <Typography variant="body2" color="info.main">
              {dashboardData.overview.avgReadingsPerUser} avg per user
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2 }}>
              <Assessment />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {dashboardData.overview.conversionRate}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Conversion Rate
            </Typography>
            <Typography variant="body2" color="warning.main">
              Users with data
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* System Health */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            System Health
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Uptime</Typography>
              <Typography variant="body2" color="success.main">
                {dashboardData.overview.systemUptime}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={dashboardData.overview.systemUptime} 
              color="success"
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Data Accuracy</Typography>
              <Typography variant="body2" color="info.main">
                {dashboardData.overview.dataAccuracy}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={dashboardData.overview.dataAccuracy} 
              color="info"
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Response Time</Typography>
              <Typography variant="body2" color="warning.main">
                {dashboardData.system.performance.responseTime}ms
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min((500 - dashboardData.system.performance.responseTime) / 500 * 100, 100)} 
              color="warning"
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        </Paper>
      </Grid>

      {/* Recent Alerts */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Alerts
          </Typography>
          <List>
            {dashboardData.alerts.slice(0, 3).map((alert) => (
              <ListItem key={alert.id} sx={{ px: 0 }}>
                <ListItemIcon>
                  {getAlertIcon(alert.type)}
                </ListItemIcon>
                <ListItemText
                  primary={alert.title}
                  secondary={alert.message}
                />
                <Chip 
                  label={alert.priority} 
                  color={getAlertColor(alert.priority)} 
                  size="small"
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Grid>

      {/* Recent Activity */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData.recentActivity.map((activity) => (
                  <TableRow key={activity.id}>
                    <TableCell>{activity.user}</TableCell>
                    <TableCell>
                      <Chip 
                        label={activity.action} 
                        size="small"
                        color={activity.type === 'user_registration' ? 'primary' : 
                               activity.type === 'blood_pressure_reading' ? 'success' : 'info'}
                      />
                    </TableCell>
                    <TableCell>{activity.details}</TableCell>
                    <TableCell>
                      {new Date(activity.timestamp).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );

  const UserAnalyticsTab = () => (
    <Grid container spacing={3}>
      {/* Demographics */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Age Distribution
          </Typography>
          {dashboardData.userMetrics.demographics.ageGroups.map((group, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{group.range}</Typography>
                <Typography variant="body2">{group.count}</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={group.percentage} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          ))}
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Gender Distribution
          </Typography>
          {dashboardData.userMetrics.demographics.genderDistribution.map((gender, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{gender.gender}</Typography>
                <Typography variant="body2">{gender.count}</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={gender.percentage} 
                color={gender.gender === 'Male' ? 'primary' : gender.gender === 'Female' ? 'secondary' : 'info'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          ))}
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Role Distribution
          </Typography>
          {dashboardData.userMetrics.demographics.roleDistribution.map((role, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{role.role}</Typography>
                <Typography variant="body2">{role.count}</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={role.percentage} 
                color={role.role === 'Patient' ? 'success' : role.role === 'Healthcare Provider' ? 'warning' : 'info'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          ))}
        </Paper>
      </Grid>

      {/* Health Metrics */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Hypertension Risk Distribution
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h5" color="success.main">
                  {dashboardData.userMetrics.health.hypertensionRisk.low}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Low Risk
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h5" color="warning.main">
                  {dashboardData.userMetrics.health.hypertensionRisk.medium}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Medium Risk
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h5" color="error.main">
                  {dashboardData.userMetrics.health.hypertensionRisk.high}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  High Risk
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'error.light' }}>
                <Typography variant="h5" color="error.dark">
                  {dashboardData.userMetrics.health.hypertensionRisk.critical}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Critical
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Reading Frequency
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Daily</Typography>
              <Typography variant="body2" color="success.main">
                {dashboardData.userMetrics.health.readingFrequency.daily}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(dashboardData.userMetrics.health.readingFrequency.daily / dashboardData.overview.totalUsers) * 100} 
              color="success"
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Weekly</Typography>
              <Typography variant="body2" color="info.main">
                {dashboardData.userMetrics.health.readingFrequency.weekly}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(dashboardData.userMetrics.health.readingFrequency.weekly / dashboardData.overview.totalUsers) * 100} 
              color="info"
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Monthly</Typography>
              <Typography variant="body2" color="warning.main">
                {dashboardData.userMetrics.health.readingFrequency.monthly}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(dashboardData.userMetrics.health.readingFrequency.monthly / dashboardData.overview.totalUsers) * 100} 
              color="warning"
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  const GeographicTab = () => (
    <Grid container spacing={3}>
      {/* Regional Distribution */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Regional Distribution
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Region</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Total Readings</TableCell>
                  <TableCell>Avg Blood Pressure</TableCell>
                  <TableCell>Market Share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData.geographic.regions.map((region, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOn sx={{ mr: 1, color: 'primary.main' }} />
                        {region.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={region.users} 
                        color="primary" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{region.readings.toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={region.avgBP} 
                        color={region.avgBP.startsWith('13') ? 'warning' : 'success'} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 100, mr: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={(region.users / dashboardData.overview.totalUsers) * 100} 
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                        <Typography variant="body2">
                          {((region.users / dashboardData.overview.totalUsers) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      {/* Country Distribution */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Country Distribution
          </Typography>
          {dashboardData.geographic.countries.map((country, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{country.name}</Typography>
                <Typography variant="body2">{country.users}</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={country.percentage} 
                color={country.name === 'England' ? 'primary' : country.name === 'Scotland' ? 'secondary' : 'info'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          ))}
        </Paper>
      </Grid>

      {/* Growth Cities */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Fastest Growing Cities
          </Typography>
          {dashboardData.geographic.cities.map((city, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{city.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="success.main" sx={{ mr: 1 }}>
                    +{city.growth}%
                  </Typography>
                  <Chip 
                    label={city.users} 
                    color="primary" 
                    size="small"
                  />
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={city.growth} 
                color="success"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          ))}
        </Paper>
      </Grid>
    </Grid>
  );

  const SystemTab = () => (
    <Grid container spacing={3}>
      {/* Performance Metrics */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 2 }}>
              <Speed />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {dashboardData.system.performance.responseTime}ms
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Response Time
            </Typography>
            <Typography variant="body2" color={dashboardData.system.performance.responseTime < 300 ? 'success.main' : 'warning.main'}>
              {dashboardData.system.performance.responseTime < 300 ? 'Good' : 'Needs Attention'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2 }}>
              <CheckCircle />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {dashboardData.system.performance.uptime}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              System Uptime
            </Typography>
            <Typography variant="body2" color="success.main">
              Excellent
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2 }}>
              <Error />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {dashboardData.system.performance.errorRate}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Error Rate
            </Typography>
            <Typography variant="body2" color="success.main">
              Low
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Storage Information */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Storage Information
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Used Space</Typography>
              <Typography variant="body2">
                {dashboardData.system.storage.usedSpace}GB / {dashboardData.system.storage.totalData}GB
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(dashboardData.system.storage.usedSpace / dashboardData.system.storage.totalData) * 100} 
              color="info"
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">Growth Rate</Typography>
            <Chip 
              label={`+${dashboardData.system.storage.growthRate}%`} 
              color="warning" 
              size="small"
            />
          </Box>
        </Paper>
      </Grid>

      {/* Security Status */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Security Status
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Compliance Score</Typography>
              <Typography variant="body2" color="success.main">
                {dashboardData.system.security.compliance}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={dashboardData.system.security.compliance} 
              color="success"
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">Active Threats</Typography>
            <Chip 
              label={dashboardData.system.security.threats} 
              color={dashboardData.system.security.threats === 0 ? 'success' : 'error'} 
              size="small"
            />
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            <AdminPanelSettings sx={{ mr: 1 }} />
            Admin Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Comprehensive analytics and system monitoring
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="365">Last year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportData}
          >
            Export Data
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="User Analytics" />
          <Tab label="Geographic" />
          <Tab label="System Health" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        {selectedTab === 0 && <OverviewTab />}
        {selectedTab === 1 && <UserAnalyticsTab />}
        {selectedTab === 2 && <GeographicTab />}
        {selectedTab === 3 && <SystemTab />}
      </Box>
    </Container>
  );
};

export default AdminPage; 